// FALTAS / PERMISOS / RETARDOS desde el PERFIL — dentro de la pestaña «Asistencia» de /profesores/:id (staff y maestros).
// Mabel, 19-oct-2026: «los retardos prefiero se puedan realizar desde el perfil y no en finanzas, son temas diferentes…
// los retardos tampoco quiero que los descuente, pero sí necesito registrarlos». SOLO REGISTRO: no toca la nómina.
// Si hay que descontar, se edita el monto de la semana en Finanzas › Nómina (lápiz junto al monto).
// Maestros: la falta a CLASE sigue siendo «No vino» en Pasar lista; aquí sirven para retardos y notas.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Info, X } from 'lucide-react';
import { getIncidencias, createIncidencia, updateIncidencia, deleteIncidencia, IncidenciasData, Incidencia, IncidKind } from '../api';
import { readCache, writeCache, pruneCacheGroup } from '../../../services/localCache';
import { useToast } from '../../../context/ToastContext';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { Skeleton } from '../../../components/Skeleton';

const pad = (n: number) => String(n).padStart(2, '0');
const fromISO = (d: string) => { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd); };
const hoyISO = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const dia = (d: string) => { const t = fromISO(d).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' }); return t.charAt(0).toUpperCase() + t.slice(1); };

const TIPOS: { key: IncidKind; label: string; cls: string; on: string }[] = [
  { key: 'falta', label: 'Falta', cls: 'bg-red-500/15 text-red-600 dark:text-red-400', on: 'border-red-500 bg-red-500/15 text-red-700 dark:text-red-300' },
  { key: 'permiso', label: 'Permiso', cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-400', on: 'border-sky-500 bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  { key: 'retardo', label: 'Retardo', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400', on: 'border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300' },
];
const tipo = (k: string) => TIPOS.find((x) => x.key === k) || TIPOS[0];

function Tile({ label, value, tone = '', testid }: { label: string; value: string | number; tone?: string; testid?: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-line px-2.5 py-1.5 sm:px-3 sm:py-2" data-testid={testid}>
      <div className="truncate text-[11px] text-content-3">{label}</div>
      <div className={`font-display text-base font-bold leading-tight sm:text-lg ${tone || 'text-content'}`}>{value}</div>
    </div>
  );
}

type Form = { id: number | null; date: string; kind: IncidKind; minutes: string; note: string };

export default function TeacherIncidencias({ userId, esProfe, from, to, mesTexto }: {
  userId: number; esProfe: boolean; from: string; to: string; mesTexto: string;
}) {
  const toast = useToast();
  const key = `teacher-incid:${userId}:${from}`;
  const [data, setData] = useState<IncidenciasData | null>(() => readCache<IncidenciasData>(key) ?? null);
  const [estado, setEstado] = useState<'ok' | 'cargando' | 'err' | 'pronto'>('cargando');
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [borrar, setBorrar] = useState<Incidencia | null>(null);
  const [rev, setRev] = useState(0);
  const formRef = useRef<HTMLDivElement>(null);
  // Al abrir el formulario (sobre todo «editar» desde una fila de abajo, en el celular) se trae a la vista.
  useEffect(() => { if (form) formRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, [form?.id, !!form]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let vivo = true;
    setData(readCache<IncidenciasData>(key) ?? null);
    setEstado('cargando');
    getIncidencias(userId, from, to)
      .then((r) => { if (!vivo) return; setData(r.data); setEstado(r.data.disponible ? 'ok' : 'pronto'); writeCache(key, r.data); pruneCacheGroup(`teacher-incid:${userId}`, key); })
      .catch((e: any) => { if (!vivo) return; setEstado(e?.status === 404 ? 'pronto' : 'err'); });
    return () => { vivo = false; };
  }, [userId, from, to, key, rev]);

  // Fecha por defecto: hoy si se está viendo este mes; si no, el día 1 del mes que se ve.
  const fechaDefault = () => { const h = hoyISO(); return h >= from && h < to ? h : from; };
  const nuevo = () => setForm({ id: null, date: fechaDefault(), kind: esProfe ? 'retardo' : 'falta', minutes: '', note: '' });
  const editar = (i: Incidencia) => setForm({ id: i.id, date: i.date, kind: i.kind, minutes: i.minutes != null ? String(i.minutes) : '', note: i.note || '' });

  async function guardar() {
    if (!form) return;
    const min = Number(form.minutes);
    if (!form.date) { toast.error('Elige la fecha'); return; }
    if (form.kind === 'retardo' && !(min >= 1 && min <= 600)) { toast.error('Escribe los minutos de retardo'); return; }
    const body = { date: form.date, kind: form.kind, minutes: form.kind === 'retardo' ? Math.round(min) : null, note: form.note.trim() || null };
    setSaving(true);
    try {
      if (form.id) await updateIncidencia(userId, form.id, body); else await createIncidencia(userId, body);
      toast.success(form.id ? 'Registro actualizado' : `${tipo(form.kind).label} registrad${form.kind === 'falta' ? 'a' : 'o'}`);
      setForm(null); setRev((x) => x + 1);
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }
  async function confirmarBorrar() {
    if (!borrar) return;
    const b = borrar; setBorrar(null);
    try { await deleteIncidencia(userId, b.id); toast.success('Registro quitado'); setRev((x) => x + 1); }
    catch (e: any) { toast.error(e?.message || 'No se pudo quitar'); }
  }

  const r = data?.resumen;
  const puede = !!data?.can_edit && data.disponible;
  const inp = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue';

  const formulario = form && (
    <div ref={formRef} className="mb-3 scroll-mt-24 rounded-xl border border-line bg-canvas p-3 shadow-inner" data-testid="incid-form">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-content">{form.id ? 'Editar registro' : 'Registrar falta / permiso / retardo'}</span>
        <button onClick={() => setForm(null)} aria-label="Cerrar" className="rounded p-1 text-content-3 hover:text-content"><X size={16} /></button>
      </div>
      <div className="mb-2 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Tipo">
        {TIPOS.map((x) => (
          <button key={x.key} type="button" role="radio" aria-checked={form.kind === x.key} onClick={() => setForm({ ...form, kind: x.key })} data-testid={`incid-tipo-${x.key}`}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${form.kind === x.key ? x.on : 'border-line bg-surface text-content-2 hover:text-content'}`}>{x.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-[10rem_8rem_1fr]">
        <label className="block text-xs text-content-3">Fecha
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`mt-1 ${inp}`} data-testid="incid-fecha" />
        </label>
        {form.kind === 'retardo' ? (
          <label className="block text-xs text-content-3">Minutos tarde
            <input type="number" min="1" max="600" inputMode="numeric" value={form.minutes} onChange={(e) => setForm({ ...form, minutes: e.target.value })} placeholder="15" className={`mt-1 ${inp} text-right`} data-testid="incid-minutos" />
          </label>
        ) : <span className="hidden sm:block" />}
        <label className="col-span-2 block text-xs text-content-3 sm:col-span-1">Nota (opcional)
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={255} placeholder={form.kind === 'permiso' ? 'p. ej. cita médica' : 'p. ej. avisó por WhatsApp'} className={`mt-1 ${inp}`} data-testid="incid-nota" />
        </label>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={() => setForm(null)} className="rounded-lg px-3 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
        <button onClick={guardar} disabled={saving} data-testid="incid-guardar" className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">{saving ? 'Guardando…' : form.id ? 'Guardar cambios' : 'Registrar'}</button>
      </div>
    </div>
  );

  return (
    <section className={esProfe ? 'mb-5 border-b border-line pb-5' : ''} data-testid="asist-incidencias">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-content">Faltas, permisos y retardos</h4>
        {puede && !form && (
          <button onClick={nuevo} data-testid="incid-nuevo"
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-on-accent hover:opacity-90"><Plus size={14} /> Registrar falta / permiso / retardo</button>
        )}
      </div>
      <p className="mb-2 flex gap-1.5 text-[12px] text-content-2" data-testid="incid-ayuda">
        <Info size={14} className="mt-0.5 shrink-0 text-content-3" />
        <span>
          Solo se registra; <b>no descuenta de la nómina</b>. Si hay que descontar, edita el monto de la semana en{' '}
          <Link to="/finanzas" className="font-medium text-accent-ink hover:underline">Finanzas › Nómina</Link>.
          {esProfe && <> Las faltas a clase se siguen marcando como <b>«No vino»</b> en Pasar lista; aquí, retardos y notas.</>}
        </span>
      </p>

      {r && (
        <div className="mb-3 grid grid-cols-4 gap-1.5 sm:gap-2 xl:grid-cols-6" data-testid="incid-contador">
          <Tile label="Faltas" value={r.faltas} tone={r.faltas ? 'text-red-600 dark:text-red-400' : ''} testid="incid-faltas" />
          <Tile label="Permisos" value={r.permisos} tone={r.permisos ? 'text-sky-700 dark:text-sky-400' : ''} testid="incid-permisos" />
          <Tile label="Retardos" value={r.retardos} tone={r.retardos ? 'text-amber-600 dark:text-amber-400' : ''} testid="incid-retardos" />
          <Tile label="Minutos" value={r.minutos} tone={r.minutos ? 'text-amber-600 dark:text-amber-400' : ''} testid="incid-minutos-total" />
        </div>
      )}

      {formulario}

      {!data && estado === 'cargando' ? (
        <Skeleton className="h-12 w-full" />
      ) : estado === 'pronto' || (data && !data.disponible) ? (
        <div className="rounded-xl bg-surface-2/60 p-3 text-sm text-content-3" data-testid="incid-pronto">Esta sección se está activando. Vuelve a abrirla en unos minutos.</div>
      ) : !data ? (
        <div className="rounded-xl bg-surface-2/60 p-3 text-sm text-content-3">No se pudieron cargar los registros.</div>
      ) : data.incidencias.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line px-3 py-3 text-sm text-content-3" data-testid="incid-vacio">
          Sin faltas, permisos ni retardos en {mesTexto}.
        </div>
      ) : (
        <div className="divide-y divide-line/60 rounded-xl border border-line" data-testid="incid-lista">
          {data.incidencias.map((i) => {
            const tp = tipo(i.kind);
            const quien = i.updated_by_name ? `editó ${i.updated_by_name}` : i.created_by_name ? `registró ${i.created_by_name}` : '';
            return (
              <div key={i.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm" data-testid="incid-fila">
                <span className="w-[6.5rem] shrink-0 text-content-2">{dia(i.date)}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tp.cls}`}>{i.kind_label}{i.kind === 'retardo' && i.minutes ? ` · ${i.minutes} min` : ''}</span>
                {i.note && <span className="order-last min-w-0 basis-full text-[13px] text-content-2 sm:order-none sm:flex-1 sm:basis-auto sm:truncate sm:text-sm" title={i.note}>{i.note}</span>}
                <span className="ml-auto flex items-center gap-1">
                  {quien && <span className="hidden text-[11px] text-content-3 sm:inline" title={i.updated_at || i.created_at || ''}>{quien}</span>}
                  {puede && (
                    <>
                      <button onClick={() => editar(i)} title="Editar" aria-label="Editar" data-testid={`incid-editar-${i.id}`} className="rounded p-1.5 text-content-3 hover:bg-surface-2 hover:text-content"><Pencil size={14} /></button>
                      <button onClick={() => setBorrar(i)} title="Quitar" aria-label="Quitar" data-testid={`incid-borrar-${i.id}`} className="rounded p-1.5 text-content-3 hover:bg-red-500/10 hover:text-red-600"><Trash2 size={14} /></button>
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {data && data.disponible && !data.can_edit && data.motivo_no_edita && (
        <p className="mt-2 text-[11px] text-content-3" data-testid="incid-solo-lectura">Solo lectura: {data.motivo_no_edita.charAt(0).toLowerCase() + data.motivo_no_edita.slice(1)}.</p>
      )}
      {borrar && (
        <ConfirmDialog title="¿Quitar este registro?" confirmLabel="Quitar"
          lines={[`${borrar.kind_label}${borrar.kind === 'retardo' && borrar.minutes ? ` de ${borrar.minutes} min` : ''} del ${dia(borrar.date).toLowerCase()}.`, 'No cambia nada en la nómina.']}
          onConfirm={confirmarBorrar} onClose={() => setBorrar(null)} />
      )}
    </section>
  );
}
